<template>
  <form @submit="onSubmit" class="space-y-4">
    <div class="mb-6">
      <h2 class="text-xl font-semibold mb-2">
        Get in touch with us today - we'd love to help you discover if this
        <span class="text-primary">{{ vehicleName }}</span> is the perfect fit for you!
      </h2>
      <p class="text-lg font-medium">Start Your Journey Here:</p>
    </div>

    <FormField v-slot="{ componentField, meta }" name="name">
      <FormItem>
        <FormLabel class="sr-only">Name</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              v-bind="componentField"
              id="name"
              type="text"
              placeholder="First and last name"
              class="w-full pl-10"
              :class="{ 'border-red-500': !meta.valid && meta.touched }"
            />
            <UserIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField, meta }" name="email">
      <FormItem>
        <FormLabel class="sr-only">Email</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              v-bind="componentField"
              id="email"
              type="email"
              placeholder="Email address"
              class="w-full pl-10"
              :class="{ 'border-red-500': !meta.valid && meta.touched }"
            />
            <MailIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField, meta }" name="phone">
      <FormItem>
        <FormLabel class="sr-only">Phone</FormLabel>
        <FormControl>
          <div class="relative">
            <Input
              v-bind="componentField"
              id="phone"
              type="tel"
              placeholder="Phone Number"
              class="w-full pl-10"
              :class="{ 'border-red-500': !meta.valid && meta.touched }"
            />
            <PhoneIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField, meta }" name="message">
      <FormItem>
        <FormLabel class="sr-only">Message</FormLabel>
        <FormControl>
          <textarea
            v-bind="componentField"
            id="message"
            rows="4"
            placeholder="Message"
            class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            :class="{ 'border-red-500': !meta.valid && meta.touched }"
          ></textarea>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div class="space-y-3">
      <FormField v-slot="{ componentField }" name="testDrive">
        <FormItem class="flex items-center justify-between bg-muted/30 p-3 rounded-md">
          <FormLabel>I would like a test drive.</FormLabel>
          <FormControl>
            <div class="flex items-center gap-2">
              <span class="text-sm text-muted-foreground">No</span>
              <Switch v-bind="{ ...componentField, modelValue: componentField.modelValue ?? false }" />
              <span class="text-sm text-muted-foreground">Yes</span>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="tradeIn">
        <FormItem class="flex items-center justify-between bg-muted/30 p-3 rounded-md">
          <FormLabel>I have a vehicle to trade in.</FormLabel>
          <FormControl>
            <div class="flex items-center gap-2">
              <span class="text-sm text-muted-foreground">No</span>
              <Switch v-bind="{ ...componentField, modelValue: componentField.modelValue ?? false }" />
              <span class="text-sm text-muted-foreground">Yes</span>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="finance">
        <FormItem class="flex items-center justify-between bg-muted/30 p-3 rounded-md">
          <FormLabel>I'm interested in finance.</FormLabel>
          <FormControl>
            <div class="flex items-center gap-2">
              <span class="text-sm text-muted-foreground">No</span>
              <Switch v-bind="{ ...componentField, modelValue: componentField.modelValue ?? false }" />
              <span class="text-sm text-muted-foreground">Yes</span>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div v-if="hasPrice" class="bg-muted/30 p-3 rounded-md">
      <FormField v-slot="{ componentField }" name="reserve">
        <FormItem class="flex items-center justify-between">
          <FormLabel class="font-medium">Reserve this Vehicle for $500?</FormLabel>
          <FormControl>
            <div class="flex items-center gap-2">
              <span class="text-sm text-muted-foreground">No</span>
              <Switch v-bind="{ ...componentField, modelValue: componentField.modelValue ?? false }" />
              <span class="text-sm text-muted-foreground">Yes</span>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="pt-4">
      <Button 
        type="submit" 
        class="w-full rounded-full mt-4 h-12 text-base bg-gray-900 hover:bg-gray-800 text-white dark:bg-gray-100 dark:hover:bg-gray-300 dark:text-gray-900"
        :disabled="props.isSubmitting"
      >
        <span v-if="props.isSubmitting" class="mr-2">
          <svg class="animate-spin h-5 w-5 text-white dark:text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
        {{ props.isSubmitting ? 'Sending...' : 'Send Enquiry' }}
      </Button>
    </div>

    <p class="text-xs text-muted-foreground text-center mt-2">
      *We do not disclose any information collected on the website to any organisation not related to this company.
    </p>
  </form>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Input } from '~/layers/base/components/ui/input'
import { Button } from '~/layers/base/components/ui/button'
import { Switch } from '~/layers/base/components/ui/switch'
import { UserIcon, MailIcon, PhoneIcon } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/layers/base/components/ui/form'

// Define Zod schema
const vehicleEnquirySchema = toTypedSchema(
  z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    phone: z.string()
      .optional()
      .refine((val) => {
        if (val === undefined || val === null || val.trim() === '') {
          return true; // Valid if optional and empty/whitespace
        }
        // If a value is provided, validate against Australian phone format
        return /^(?:\+?61|0)(?:4(?:[ -]?\d){8}|[2378](?:[ -]?\d){8})$/.test(val.trim());
      }, {
        message: 'Please use a valid Australian phone number (e.g., 04xx xxx xxx or 0x xxxx xxxx).',
      }),
    message: z.string().optional(),
    testDrive: z.boolean().default(() => false),
    tradeIn: z.boolean().default(() => false),
    finance: z.boolean().default(() => false),
    reserve: z.boolean().default(() => false)
  })
)

// Define proper interface for vehicle object
interface VehicleInterface {
  id?: string;
  title?: string;
  price?: number | null;
  make?: {
    displayValue?: string[];
  };
  model?: {
    displayValue?: string[];
  };
  [key: string]: any;
}

const props = defineProps({
  vehicle: {
    type: Object as () => VehicleInterface,
    default: () => ({})
  },
  isSubmitting: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit'])

// Vee-validate form setup
const { handleSubmit, resetForm } = useForm({
  validationSchema: vehicleEnquirySchema,
  initialValues: {
    name: '',
    email: '',
    phone: '',
    message: '',
    testDrive: false,
    tradeIn: false,
    finance: false,
    reserve: false
  }
})

const vehicleName = computed(() => {
  if (!props.vehicle) return 'this vehicle'
  
  if (props.vehicle.make?.displayValue?.[0] && props.vehicle.model?.displayValue?.[0]) {
    return `${props.vehicle.make.displayValue[0]} ${props.vehicle.model.displayValue[0]}`
  }
  
  return props.vehicle.title || 'this vehicle'
})

const hasPrice = computed(() => {
  return !!props.vehicle?.price
})

const onSubmit = handleSubmit(values => {
  // Add vehicle ID to form data for tracking
  const submitData = {
    ...values, // Use validated values from vee-validate
    vehicleId: props.vehicle?.id,
    vehicleName: vehicleName.value
  }
  
  // Emit submit event to parent component
  emit('submit', submitData)
  
  // Reset form using vee-validate's resetForm
  resetForm()
})
</script> 